import { z } from "zod/v4";

import { type Tuple } from "../Tuple";
import { ZSet, dehydrateZSet, hydrateZSet, rawZSetSchema } from "../ZSet";
import { type UnaryNonlinearOperatorLogic } from "./UnaryNonlinearOperatorLogic";
import { type JsonPathRef, jsonpathRefSchema } from "./schema-util";

export function tupleComparatorFromOrderBySpec<Row>(
	spec: OrderedTopKSpec
): (row: Tuple<Row>) => Tuple<unknown> {
	return (row: Tuple<Row>): Tuple<unknown> =>
		spec.orderBy ? row.pluck(spec.orderBy.path) : (row as Tuple<unknown>);
}

export function orderedTopK<Row>(
	spec: OrderedTopKSpec
): UnaryNonlinearOperatorLogic<Row, Row, OrderedTopKState<Row>> {
	const orderBy = tupleComparatorFromOrderBySpec<Row>(spec);

	return (
		input: ZSet<Row>,
		state: OrderedTopKState<Row> = emptyOrderedTopKState()
	): [ZSet<Row>, OrderedTopKState<Row>] => {
		const newState: OrderedTopKState<Row> = {
			type: "orderedTopK",
			orderedRows: state.orderedRows,
		};

		// Update state with new input.
		for (const ztuple of input) {
			newState.orderedRows = newState.orderedRows.add(ztuple);
		}

		// Inefficiently get top K rows. We will use a min-heap for this later.
		newState.orderedRows = newState.orderedRows.sort(orderBy, spec.direction ?? "ASC");

		const oldTopK = state.orderedRows.filter((ztuple) => ztuple.weight > 0).take(spec.k);
		const newTopK = newState.orderedRows.filter((ztuple) => ztuple.weight > 0).take(spec.k);

		let out = ZSet.empty<Row>();
		for (const ztuple of oldTopK) {
			if (!newTopK.has(ztuple.tuple)) {
				out = out.add({ ...ztuple, weight: -1 });
			}
		}
		for (const ztuple of newTopK) {
			if (!oldTopK.has(ztuple.tuple)) {
				out = out.add({ ...ztuple, weight: 1 });
			}
		}
		out = out.sort(orderBy, spec.direction ?? "ASC");

		return [out, newState];
	};
}

//
// Enriched OrderedTopK State.
//

export type OrderedTopKState<Row> = {
	type: "orderedTopK";
	orderedRows: ZSet<Row>;
};

export const emptyOrderedTopKState = <Row>(): OrderedTopKState<Row> => ({
	type: "orderedTopK",
	orderedRows: ZSet.empty<Row>(),
});

export const hydrateOrderedTopKState = <Row>(
	state: RawOrderedTopKState
): OrderedTopKState<Row> => ({
	type: "orderedTopK",
	orderedRows: hydrateZSet(state.orderedRows),
});

export const dehydrateOrderedTopKState = <Row>(
	state: OrderedTopKState<Row>
): RawOrderedTopKState => ({
	type: "orderedTopK",
	orderedRows: dehydrateZSet(state.orderedRows),
});

//
// Raw OrderedTopK State.
//

export const rawOrderedTopKStateSchema = z.object({
	type: z.literal("orderedTopK"),
	orderedRows: rawZSetSchema,
});
export type RawOrderedTopKState = z.infer<typeof rawOrderedTopKStateSchema>;

//
// OrderedTopK Spec.
//

export const orderedTopKSpecSchema = z.object({
	type: z.literal("orderedTopK"),
	orderBy: jsonpathRefSchema.optional(),
	k: z.number().optional(),
	direction: z.enum(["ASC", "DESC"]).optional(),
});
export type OrderedTopKSpec = z.infer<typeof orderedTopKSpecSchema>;

export const orderedTopKSpec = (
	orderBy: JsonPathRef,
	k?: number,
	direction?: "ASC" | "DESC"
): OrderedTopKSpec => ({
	type: "orderedTopK",
	orderBy,
	k,
	direction,
});
